import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  HeartIcon,
  UserIcon,
  CalendarIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { useAuth0 } from '@auth0/auth0-react'
import { isAdmin } from '../../utils/roles'
import { memorialApi, Memorial } from '../../services/memorialApi'
import CreateMemorialModal from '../../components/CreateMemorialModal'
import EditMemorialModal from '../../components/EditMemorialModal'
import RetryImage from '../../components/RetryImage'
import toast from 'react-hot-toast'
import { useState, useEffect } from 'react'

export default function MemorialPage() {
  const { user, getAccessTokenSilently } = useAuth0()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedMemorial, setSelectedMemorial] = useState<Memorial | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  
  const { data: memorialsData, isLoading, error, refetch } = useQuery({
    queryKey: ['memorials'],
    queryFn: () => memorialApi.getMemorials(getAccessTokenSilently),
  })

  useEffect(() => {
    if (error) {
      toast.error('Failed to load memorial data')
    }
  }, [error])

  const handleCreateMemorial = async (memorialData: any) => {
    try {
      setIsCreating(true)
      await memorialApi.createMemorial(memorialData, getAccessTokenSilently)
      toast.success('Memorial created successfully')
      setShowCreateModal(false)
      refetch() // Refresh the list
    } catch (error: any) {
      toast.error(error.message || 'Failed to create memorial')
    } finally {
      setIsCreating(false)
    }
  }

  const handleEditMemorial = (memorial: Memorial) => {
    setSelectedMemorial(memorial)
    setShowEditModal(true)
  }

  const handleUpdateMemorial = async (id: string, memorialData: any) => {
    try {
      setIsUpdating(true)
      await memorialApi.updateMemorial(id, memorialData, getAccessTokenSilently)
      toast.success('Memorial updated successfully')
      setShowEditModal(false)
      setSelectedMemorial(null)
      refetch() // Refresh the list
    } catch (error: any) {
      toast.error(error.message || 'Failed to update memorial')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDeleteMemorial = async (memorial: Memorial) => {
    if (!confirm(`Are you sure you want to delete the memorial for ${memorial.name}? This action cannot be undone.`)) {
      return
    }

    try {
      await memorialApi.deleteMemorial(memorial.id, getAccessTokenSilently)
      toast.success('Memorial deleted successfully')
      refetch() // Refresh the list
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete memorial')
    }
  }

  return (
    <div className="min-h-screen bg-rb-dark py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex items-center justify-between mb-4">
            <Link
              to="/legacy"
              className="inline-flex items-center text-white hover:text-gray-200 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Legacy
            </Link>
            
            {isAdmin(user) && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 bg-rb-gold text-rb-dark px-4 py-2 rounded-lg hover:bg-yellow-400 transition-colors font-medium"
              >
                <PlusIcon className="h-5 w-5" />
                Add Memorial
              </button>
            )}
          </div>
          
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-red-500 rounded-full">
                <HeartIcon className="w-12 h-12 text-white" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">In Memoriam</h1>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              Honoring the memory of beloved Midwest Railbirds members who have passed away. 
              Their contributions to our community and their love for the sport will never be forgotten.
            </p>
          </div>
        </motion.div>

        {/* Memorial Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-12 border border-white/20"
        >
          <div className="text-center">
            <HeartIcon className="w-8 h-8 text-red-400 mx-auto mb-4" />
            <p className="text-white/90 text-lg italic leading-relaxed">
              "Those we love never truly leave us. They live on in our memories, in the traditions they helped create, 
              and in the Derby spirit they shared with all of us. Their legacy continues in every gathering, 
              every story told, and every new member welcomed into our Railbirds family."
            </p>
          </div>
        </motion.div>

        {/* Memorial Members */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rb-gold mx-auto"></div>
            <p className="text-white/70 mt-4">Loading memorial data...</p>
          </div>
        ) : memorialsData?.memorials && memorialsData.memorials.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-8"
          >
            {memorialsData.memorials.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border-l-4 border-red-500"
              >
                <div className="p-8">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-6 flex-1">
                      <div className="flex-shrink-0">
                      <div className="w-20 h-20 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center overflow-hidden">
                        {member.photo ? (
                          <RetryImage 
                            src={member.photo} 
                            alt={member.name}
                            className="w-full h-full object-cover"
                            fallbackIcon={<UserIcon className="w-10 h-10 text-gray-400" />}
                          />
                        ) : (
                          <UserIcon className="w-10 h-10 text-gray-400" />
                        )}
                      </div>
                      </div>
                      
                      <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        {member.name}
                      </h3>
                      
                      <div className="flex items-center space-x-4 mb-4 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center">
                          <CalendarIcon className="w-4 h-4 mr-1" />
                          Member: {member.memberSince} - {member.passedAway}
                        </div>
                        <div>
                          {member.yearsActive} years with us
                        </div>
                      </div>
                      
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                        {member.bio}
                      </p>
                      
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                          Contributions to Our Community
                        </h4>
                        <ul className="space-y-2">
                          {member.contributions.map((contribution, idx) => (
                            <li key={idx} className="flex items-center text-gray-600 dark:text-gray-300">
                              <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                              {contribution}
                            </li>
                          ))}
                        </ul>
                      </div>
                      </div>
                    </div>
                    
                    {/* Admin Actions */}
                    {isAdmin(user) && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditMemorial(member)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Edit memorial"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteMemorial(member)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete memorial"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center py-16"
          >
            <HeartIcon className="w-16 h-16 text-white/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              No Memorial Records Yet
            </h3>
            <p className="text-white/60">
              We are blessed that all our founding members are still with us.
            </p>
          </motion.div>
        )}

        {/* Create Memorial Modal */}
        <CreateMemorialModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreateMemorial={handleCreateMemorial}
          isLoading={isCreating}
        />

        {/* Edit Memorial Modal */}
        <EditMemorialModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false)
            setSelectedMemorial(null)
          }}
          memorial={selectedMemorial}
          onUpdateMemorial={handleUpdateMemorial}
          isLoading={isUpdating}
        />

      </div>
    </div>
  )
}
